import { useQuery } from "react-query";
import { request } from "../../config/helpers/axios-instance";
import { throwIfRequestError } from "./request-error";

// useOrdersList — GET /api/userOrder/admin, the single endpoint behind three screens.
//
// Orders, B2B, and Reorders are the same query with different parameters, which is why
// the legacy Partner Orders page is only 87 lines: it renders the shared orders table
// and adds a `partnerId`. Rather than repeat that endpoint's shape-mapping three times,
// this hook owns it once and each screen passes its own filters.
//
// Reorders is the exception: it hits a *different* URL (`futureOrders/:days`) because
// "supply is running out" is a server-side calculation (end date window, delivered
// status, plus a NOT EXISTS check for an existing successor). Pass `daysLeft` to switch
// to it. The response shape is identical, so the mapping below is shared.
//
// Shape note: this endpoint returns a heavy row — ~30 own columns plus 7 eager-loaded
// associations, realistically 80-150 scalar fields. Everything the tables need is
// flattened here so no component reaches into `mapUserPatient.Patient.contact_no`
// itself; if the association shape changes, this is the one place to fix.

/** MyTatva user objects use snake_case and are hydrated post-query. */
function personName(person) {
    return person?.name || null;
}

function personContact(person) {
    return person?.contact_no || null;
}

/** Flatten one admin order row into what the OPS tables actually render. */
export function toOrderRow(order) {
    const patient = order.mapUserPatient?.Patient;
    const caregiver = order.mapUserPatient?.User;
    const address = order.address || {};
    // Only the first delivery matters for a list row; reshipments create extras.
    const delivery = Array.isArray(order.deliveries) ? order.deliveries[0] : null;

    return {
        id: order.id,
        orderId: order.id,
        status: order.status,
        orderType: order.type,
        // `previousOrderId` is the canonical reorder test — the `_REORDER` type suffix
        // exists too, but lineage is the reliable one. Both are now returned by the
        // endpoint (they were create-only until this work).
        isReorder: Boolean(order.previousOrderId),
        rootOrderId: order.rootOrderId,
        previousOrderId: order.previousOrderId,

        patientName: personName(patient),
        contactNumber: personContact(patient),
        caregiverName: personName(caregiver),

        address: address.line || null,
        city: address.district || null,
        state: address.state || null,
        pincode: address.pincode || null,

        partnerId: order.partnerId,
        partnerName: order.partner?.name || null,
        partnerEntityId: order.partnerEntityId,
        source: order.source,
        channel: order.channel,

        orderDate: order.createdAt,
        startDate: order.startDate,
        endDate: order.endDate,
        duration: order.noOfDays,

        inventoryId: order.inventoryId,
        notes: order.notes,
        cancelledReason: order.cancelledReason,
        total: order.total,
        discount: order.discount,
        shippingCharges: order.shippingCharges,

        prescriptionId: order.prescriptionId,
        prescriptionLinks: order.prescription?.links || [],

        deliveryId: delivery?.id || null,
        delivery,
        // The normalized DTO pillup-api's GET /userOrder/admin now attaches per row
        // (classes/UserOrder/Functions.js:getAdmin -> classes/Delivery/normalize.js) --
        // resolves Shiprocket/DTDC's different courierDetails key names into one shape.
        // null when there's no delivery record, same as `delivery` above.
        deliveryDetails: order.deliveryDetails || null,
        payments: order.payments || [],
        paymentItems: order.paymentItems || [],

        raw: order,
    };
}

/**
 * Build the wire params. Only set values are sent — the endpoint filters on whatever it
 * receives, so an empty string would filter for empty rather than "don't filter".
 */
export function buildOrderParams({ status, partnerId, hasPartner, type, search, from, to, orderBy, order } = {}) {
    const params = {};
    const statusList = Array.isArray(status) ? status : status ? [status] : [];
    if (statusList.length) params.status = statusList.join(",");
    if (partnerId) params.partnerId = partnerId;
    // "Partnered, no matter which" — `partnerId` narrows to ONE partner, so this is what
    // the B2B console's All view uses. Server-side (pillup-api getAdmin), so the row
    // count and the totals agree; it replaced a client-side filter that left the pager
    // counting every order while showing only the partnered few.
    if (hasPartner) params.hasPartner = "true";
    if (type) params.type = type;
    // The endpoint searches MyTatva by name; it reads `userName` regardless of which
    // key you send, so sending both is pointless — send the one it reads.
    if (search) params.userName = search;
    if (from) params.createdAt = from;
    if (to) params.createdTill = to;
    if (orderBy) params.orderBy = orderBy;
    if (order) params.order = order;
    return params;
}

/**
 * @param {object} filters
 * @param {number} [filters.page]
 * @param {number} [filters.size]
 * @param {number} [filters.daysLeft] - switches to the reorder-candidates endpoint.
 * @param {boolean} [filters.enabled]
 */
export default function useOrdersList(filters = {}) {
    const { page = 1, size = 20, daysLeft, enabled = true } = filters;
    const params = { ...buildOrderParams(filters), page, size };
    const url = daysLeft ? `/userOrder/admin/futureOrders/${daysLeft}` : "/userOrder/admin";

    return useQuery(
        ["ops-orders", url, params],
        async () => {
            const result = await request({ url, method: "GET", params });
            throwIfRequestError(result);
            const body = result.data;
            // pillup-api answers HTTP 200 even on failure — errors are in-band, so the
            // HTTP-level check above cannot see them.
            if (!body?.success) throw body;

            const { count, rows } = body.data;
            return {
                total: Number(count) || 0,
                orders: (rows || []).map(toOrderRow),
                pagination: { page, size, total: Number(count) || 0 },
            };
        },
        { keepPreviousData: true, retry: false, enabled }
    );
}
